import React, { useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { 
    Box, 
    Typography, 
    TextField,
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid
} from '@mui/material';
import { format, setYear, setMonth, getYear, getMonth } from 'date-fns';

const DateRangeFilter = ({ dateFrom, dateTo, onDateFromChange, onDateToChange, disabled }) => {
    const [openFromDialog, setOpenFromDialog] = useState(false);
    const [openToDialog, setOpenToDialog] = useState(false);
    const [tempFromDate, setTempFromDate] = useState(dateFrom);
    const [tempToDate, setTempToDate] = useState(dateTo);

    // Generate year options (e.g., from 2020 to 2030)
    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
    
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const handleFromChange = (newDate) => {
        if (newDate && !isNaN(newDate.getTime())) {
            onDateFromChange(newDate);
        }
    };

    const handleToChange = (newDate) => {
        if (newDate && !isNaN(newDate.getTime())) {
            onDateToChange(newDate);
        }
    };

    const handleFromAccept = () => {
        handleFromChange(tempFromDate);
        setOpenFromDialog(false);
    };

    const handleToAccept = () => {
        handleToChange(tempToDate);
        setOpenToDialog(false);
    };

    const formatDateDisplay = (date) => {
        if (!date || !isNaN(new Date(date).getTime()) === false) return '';
        return format(date, 'MMM dd, yyyy');
    };

    const handleYearChange = (year, isFromDate) => {
        const date = isFromDate ? tempFromDate : tempToDate;
        const newDate = setYear(date || new Date(), year);
        if (isFromDate) {
            setTempFromDate(newDate);
        } else {
            setTempToDate(newDate);
        }
    };

    const handleMonthChange = (monthIndex, isFromDate) => {
        const date = isFromDate ? tempFromDate : tempToDate;
        const newDate = setMonth(date || new Date(), monthIndex);
        if (isFromDate) {
            setTempFromDate(newDate);
        } else {
            setTempToDate(newDate);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ width: '100%' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Release Date Range
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    {/* From Date Field */}
                    <TextField
                        label="From"
                        value={formatDateDisplay(dateFrom)}
                        onClick={() => !disabled && setOpenFromDialog(true)}
                        size="small"
                        fullWidth
                        disabled={disabled}
                        InputProps={{
                            readOnly: true,
                        }}
                        sx={{ cursor: disabled ? 'default' : 'pointer' }}
                    />

                    {/* To Date Field */}
                    <TextField
                        label="To"
                        value={formatDateDisplay(dateTo)}
                        onClick={() => !disabled && setOpenToDialog(true)}
                        size="small"
                        fullWidth
                        disabled={disabled}
                        InputProps={{
                            readOnly: true,
                        }}
                        sx={{ cursor: disabled ? 'default' : 'pointer' }}
                    />

                    {/* From Date Dialog */}
                    <Dialog
                        open={openFromDialog}
                        onClose={() => {
                            setTempFromDate(dateFrom);
                            setOpenFromDialog(false);
                        }}
                        maxWidth="xs"
                        fullWidth
                    >
                        <DialogContent>
                            {/* Custom Year/Month Selectors */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Year</InputLabel>
                                        <Select
                                            value={getYear(tempFromDate || new Date())}
                                            onChange={(e) => handleYearChange(e.target.value, true)}
                                            label="Year"
                                        >
                                            {years.map(year => (
                                                <MenuItem key={year} value={year}>
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Month</InputLabel>
                                        <Select
                                            value={getMonth(tempFromDate || new Date())}
                                            onChange={(e) => handleMonthChange(e.target.value, true)}
                                            label="Month"
                                        >
                                            {months.map((month, index) => (
                                                <MenuItem key={month} value={index}>
                                                    {month}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            
                            {/* Date Picker with day view only */}
                            <StaticDatePicker
                                displayStaticWrapperAs="desktop"
                                value={tempFromDate}
                                onChange={setTempFromDate}
                                views={['day']}
                                openTo="day"
                                slotProps={{
                                    actionBar: {
                                        actions: [],
                                    },
                                }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => {
                                setTempFromDate(dateFrom);
                                setOpenFromDialog(false);
                            }}>
                                Cancel
                            </Button>
                            <Button onClick={handleFromAccept} variant="contained">
                                OK
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* To Date Dialog */}
                    <Dialog
                        open={openToDialog}
                        onClose={() => {
                            setTempToDate(dateTo);
                            setOpenToDialog(false);
                        }}
                        maxWidth="xs"
                        fullWidth
                    >
                        <DialogContent>
                            {/* Custom Year/Month Selectors */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Year</InputLabel>
                                        <Select
                                            value={getYear(tempToDate || new Date())}
                                            onChange={(e) => handleYearChange(e.target.value, false)}
                                            label="Year"
                                        >
                                            {years.map(year => (
                                                <MenuItem key={year} value={year}>
                                                    {year}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Month</InputLabel>
                                        <Select
                                            value={getMonth(tempToDate || new Date())}
                                            onChange={(e) => handleMonthChange(e.target.value, false)}
                                            label="Month"
                                        >
                                            {months.map((month, index) => (
                                                <MenuItem key={month} value={index}>
                                                    {month}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            
                            {/* Date Picker with day view only */}
                            <StaticDatePicker
                                displayStaticWrapperAs="desktop"
                                value={tempToDate}
                                onChange={setTempToDate}
                                minDate={dateFrom}
                                views={['day']}
                                openTo="day"
                                slotProps={{
                                    actionBar: {
                                        actions: [],
                                    },
                                }}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => {
                                setTempToDate(dateTo);
                                setOpenToDialog(false);
                            }}>
                                Cancel
                            </Button>
                            <Button onClick={handleToAccept} variant="contained">
                                OK
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            </Box>
        </LocalizationProvider>
    );
};

export default DateRangeFilter;